import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { ContactUSAPI } from '../../../../services/contactus.services';
import { removeAllProjectsEvent } from '../../../../redux/slices/donation';
import { setIsGiftCardOpenEvent, setIsValidEvent, setVoucherCodeEvent } from '../../../../redux/slices/voucher';
import GIFT_CARDS from '../../../../utils/giftcards';
import { resetGiftCardDetails } from '../../../../redux/slices/giftCard';
import { FormProvider, RHFSelect, RHFTextField } from '../../../../components/hook-form';
import Image from '../../../../components/Image';
import { CloseIcon } from '../../../../theme/overrides/CustomIcons';
import { consumeVoucher } from '../../../../services/voucher.services';
import { DonationAPI } from '../../../../services/donation.services';

const STORAGE_KEY = 'voucherGiftCardForm';
const PLACEHOLDER_MSG = 'lorem ipsum dolor';

/* ---- localStorage helpers (unchanged) */
const loadStoredForm = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const saveStoredForm = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    console.error('Failed to save form data');
  }
};
const clearStoredForm = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    console.error('Failed to clear form data');
  }
};

export default function VoucherGiftCardModal() {
  const dispatch = useDispatch();
  const { isGiftCardOpen: isOpen, voucherCode } = useSelector((state) => state.voucher);
  const { projects } = useSelector((store) => store.donation);
  const { enqueueSnackbar } = useSnackbar();

  const [error, setFormError] = useState();
  const [isLoading, setIsLoading] = useState(false);

  /* ✅ Mobile breakpoint helpers (no UI change, only behavior) */
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmDown = useMediaQuery(theme.breakpoints.down('md'));

  /* hydrate from storage (falls back to false) */
  const stored = loadStoredForm();
  const [isFromAnonymous, setIsFromAnonymous] = useState(!!stored?.isFromAnonymous);
  const [isDonorAnonymous, setIsDonorAnonymous] = useState(
    typeof stored?.isDonorAnonymous !== 'undefined' ? !!stored.isDonorAnonymous : !!stored?.isAnonymous
  );

  const [project, setProject] = useState();
  useEffect(() => {
    if (projects?.length) {
      const formatted = Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(100);
      setProject({ ...projects.at(0), amount: formatted });
    } else {
      setProject();
    }
  }, [projects]);

  const resolver = yupResolver(
    Yup.object()
      .shape({
        /* donor validation depends on donor anonymous */
        firstName: isDonorAnonymous ? Yup.string() : Yup.string().required('Required'),
        lastName: isDonorAnonymous ? Yup.string() : Yup.string().required('Required'),
        email: isDonorAnonymous ? Yup.string() : Yup.string(),
        phone: isDonorAnonymous ? Yup.string() : Yup.string(),
        /* gift card fields */
        message: Yup.string().required(),
        from: Yup.string().required('Required'),
        fromEmail: Yup.string().required('Required'),
        to: Yup.string().required('Required'),
        toEmail: Yup.string().required('Required'),
        occasion: Yup.string(),
        giftCard: Yup.string(),
      })
      .test((e) => {
        if (!isDonorAnonymous && !e.phone && !e.email) {
          setFormError('Please enter either email or phone number');
        }
        return isDonorAnonymous || !!e.email || !!e.phone;
      })
  );

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, {
        variant: 'error',
        onExit: () => setFormError(),
      });
    }
  }, [error, enqueueSnackbar]);

  /* ---- Default values */
  const defaultValues = useMemo(
    () => ({
      occasion: '',
      giftCard: '',
      message: PLACEHOLDER_MSG,
      from: '',
      fromEmail: '',
      to: '',
      toEmail: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    }),
    []
  );

  /* ---- Initialize from localStorage if available */
  const initialValues = stored ? { ...defaultValues, ...stored } : defaultValues;

  const methods = useForm({
    resolver,
    defaultValues: initialValues,
  });

  const { watch, reset, setValue, setError, handleSubmit, getValues } = methods;
  const values = watch();

  /* If Your Info anonymous was stored, ensure fields are set */
  useEffect(() => {
    if (isFromAnonymous) {
      if (values.from !== 'Anonymous') setValue('from', 'Anonymous');
      if (values.fromEmail !== 'admin@charityphilippines.org') {
        setValue('fromEmail', 'admin@charityphilippines.org');
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isFromAnonymous]);

  /* ---- Only set default message on occasion change IF message is empty or still placeholder */
  const prevOccasionRef = useRef(values.occasion);
  useEffect(() => {
    const prevOcc = prevOccasionRef.current;
    const occ = values.occasion;
    if (occ && occ !== prevOcc) {
      const userMessage = values.message;
      const shouldFillDefault =
        !userMessage || userMessage.trim() === '' || userMessage === PLACEHOLDER_MSG;
      if (shouldFillDefault) {
        setValue('message', GIFT_CARDS[occ]?.defaultMessage ?? '');
      }
      prevOccasionRef.current = occ;
    }
  }, [values.occasion, values.message, setValue]);

  /* ---- Persist every change to localStorage (incl. both anonymous flags) */
  useEffect(() => {
    const sub = watch((formValues) => {
      saveStoredForm({ ...formValues, isFromAnonymous, isDonorAnonymous });
    });
    return () => sub.unsubscribe();
  }, [watch, isFromAnonymous, isDonorAnonymous]);

  /* ---- Persist whenever either anonymous toggles */
  useEffect(() => {
    const current = getValues();
    saveStoredForm({ ...current, isFromAnonymous, isDonorAnonymous });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isFromAnonymous, isDonorAnonymous]);

  const onClose = () => {
    dispatch(setIsGiftCardOpenEvent(false));
  };

  const onSubmit = async (data) => {
    if (!project) {
      setFormError('Please select a project first.');
      return;
    }

    const donorData = isDonorAnonymous
      ? {
          firstName: 'Anonymous',
          lastName: 'Anonymous',
          email: 'anonymous@user.com',
          phone: '09111111111',
        }
      : {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
        };

    setIsLoading(true);
    try {
      await consumeVoucher({
        ...donorData,
        donor: donorData.email ?? donorData.phone ?? 'anonymous',
        code: voucherCode,
      });

      const payload = createPayload({ formData: donorData, projects: [project] });

      const giftCardPayload = {
        message: data.message,
        from: data.from,
        fromEmail: data.fromEmail,
        to: data.to,
        toEmail: data.toEmail,
        occasion: data.occasion,
        giftCard: data.giftCard,
      };

      await DonationAPI.submitVoucherDonation({ details: payload, voucherCode, giftCard: giftCardPayload });
      await ContactUSAPI.sendGiftCard({
        ...giftCardPayload,
        donations: [{ project: project.name, amount: 100, id: project._id }],
      });

      clearStoredForm();
      reset({ occasion: '' });
      setIsFromAnonymous(false);
      setIsDonorAnonymous(false);

      /* cleanup any stored intent after success */
      try { localStorage.removeItem('voucherDonationType'); } catch { console.error('Failed to clear voucherDonationType'); }

      enqueueSnackbar('🎉 Your Gift Card has been sent successfully!');
      dispatch(setVoucherCodeEvent(null));
      dispatch(setIsValidEvent(false));
      dispatch(removeAllProjectsEvent());
      onClose();
      window.location = '/resultpage/0/success';
    } catch (e) {
      setFormError(e.message);
    }
    setIsLoading(false);
  };

  /* ---- Visibility flags (no UI change, only logic) */
  const hasSelectedCard = !!values.giftCard;
  const hasRecipient = !!values.to?.trim() && !!values.toEmail?.trim();
  const hasSender = isFromAnonymous || (!!values.from?.trim() && !!values.fromEmail?.trim());
  const hasMessage = !!values.message?.trim();

  const canShowStep2 = hasSelectedCard;
  const canShowStep3 = hasSelectedCard && hasRecipient && hasSender && hasMessage;

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={isOpen}
      /* ✅ Fullscreen on phones; same look on larger screens */
      fullScreen={isXs}
      /* Keep scroll smooth on mobile */
      scroll="paper"
      PaperProps={{
        sx: {
          /* tighter padding on mobile so content fits comfortably */
          borderRadius: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle>
        <Box
          p={1}
          maxWidth={'95%'}
          bgcolor="#f8f8f8ff"
          borderRadius={1}
          /* prevent overflow on tiny screens */
          sx={{ mx: { xs: 0, sm: 'auto' } }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item xs>
              <Typography fontWeight={600} color="black">Voucher Code In Use:</Typography>
            </Grid>
            <Grid item>
              <Typography fontWeight={600} color="black" sx={{ wordBreak: 'break-all' }}>
                {voucherCode}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: { xs: 8, sm: 20 },
            top: { xs: 8, sm: 22 },
            color: (t) => t.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        /* ✅ No horizontal scroll on mobile, taller viewport usage */
        sx={{
          overflowX: 'hidden',
          maxHeight: { xs: 'unset', sm: '70vh' },
          pb: { xs: 4, sm: 3 },
        }}
      >
        {/* Banner + Title section */}
        <Stack
          direction={{ xs: 'column', md: 'row-reverse' }} /* same visual order, stacked on mobile */
          alignItems="center"
          spacing={{ xs: 2, md: 0 }}
          sx={{ my: '10px' }}
        >
          <Box
            component="img"
            src="/images/gift-cards/giftCardBanner.jpg"
            alt="Gift Card Banner"
            sx={{
              width: { xs: '100%', md: '40%' },
              maxWidth: '100%',
              display: 'block',
            }}
          />
          <Box
            /* remove negative margins; use responsive padding instead */
            sx={{
              width: { xs: '100%', md: '50%' },
              pl: { xs: 0, md: 5 },
              mr: { xs: 0, md: -2 },
            }}
          >
            <Typography
              variant="h3"
              mb={2}
              textAlign="center"
              sx={{ fontFamily: "'Dancing Script', cursive", fontWeight: 600, fontSize: { xs: '2rem', md: '3rem' } }}
            >
              Send a Gift Card
            </Typography>

            <Typography
              variant="h4"
              mb={2}
              textAlign="center"
              sx={{ fontFamily: "'Dancing Script', cursive", fontSize: { xs: '1.25rem', md: '2rem' } }}
            >
              ✨ Mark special moments in a meaningful way. 💌
              Your message reaches your loved one ❤️,
              while your gesture extends kindness to communities 🌍💖.
              <br />
            </Typography>
            <Typography variant="h6" textAlign="center">
              All in just <strong>3 simple steps.</strong>
            </Typography>
          </Box>
        </Stack>

        <Box mt={{ xs: 5, sm: 5 }} mx={{ xs: 1, sm: 5 }}>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box display="flex" alignItems="center" mb={2}>
              <Typography mr={1} variant="button" sx={{ color: '#0fa058', fontWeight: 700 }}>
                STEP 1.
              </Typography>
              <Typography variant="button" sx={{ fontWeight: 600 }}>
                Select your card design
              </Typography>
            </Box>

            <RHFSelect name="occasion" placeholder="Please select" sx={{ mt: 1, width: { xs: '100%', sm: 320 } }}>
              <option value="">Please Select</option>
              <option value="birthday">Birthday</option>
              <option value="anniversary">Wedding Anniversary</option>
              <option value="valentines">Valentines</option>
              <option value="mothersday">Mother's Day</option>
              <option value="fathersday">Father's Day</option>
              <option value="graduation">Graduation</option>
              <option value="christmas">Christmas</option>
              <option value="newyear">New Year</option>
              <option value="memorial">Memorial</option>
            </RHFSelect>

            {!values.occasion && (
              <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {Object.values(GIFT_CARDS)
                  .map((e) => e.designs.at(1))
                  .map((e, idx) => (
                    <GiftCardDesignItem
                      key={`${e?.preview || 'card'}-${idx}`}
                      src={e?.preview}
                      onClick={() => setError('occasion', { message: 'Please select an occasion', type: 'validate' })}
                      disabled
                    />
                  ))}
              </Box>
            )}

            <Box
              sx={{
                mt: 5,
                display: 'grid',
                gap: 16 / 8,
                gridTemplateColumns: {
                  xs: '1fr 1fr', /* two per row on phones */
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(4, 1fr)',
                },
              }}
            >
              {GIFT_CARDS[values.occasion]?.designs?.map((e) => (
                <GiftCardDesignItem
                  key={e.preview}
                  src={e.preview}
                  onClick={() => setValue('giftCard', e.value)}
                  selected={values.giftCard === e.value}
                />
              ))}
            </Box>

            {canShowStep2 && (
              <>
                <Box display="flex" mt={{ xs: 6, sm: 10 }} alignItems="center" mb={2}>
                  <Typography mr={1} variant="button" sx={{ color: '#0fa058', fontWeight: 700 }}>
                    STEP 2.
                  </Typography>
                  <Typography variant="button" sx={{ fontWeight: 600 }}>
                    Your Greeting & Info
                  </Typography>
                </Box>

                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12} md={6}>
                    <RHFTextField fullWidth name="to" label="Recipient’s Name" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFTextField fullWidth name="toEmail" label="Recipient’s Email" />
                  </Grid>
                </Grid>

                <RHFTextField
                  sx={{ my: 2 }}
                  name="message"
                  multiline
                  minRows={4}
                  label="Your Message"
                  fullWidth
                />

                <Box display="flex" mt={2} mx={{ xs: 0, sm: 1 }} flexDirection="column">
                  <Typography variant="button" sx={{ fontWeight: 600 }}>
                    Your Info
                  </Typography>

                  <FormControlLabel
                    sx={{ mt: 1, mb: 2 }}
                    control={
                      <Checkbox
                        checked={isFromAnonymous}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setIsFromAnonymous(checked);
                          if (checked) {
                            setValue('from', 'Anonymous');
                            setValue('fromEmail', 'admin@charityphilippines.org');
                          } else {
                            setValue('from', '');
                            setValue('fromEmail', '');
                          }
                        }}
                      />
                    }
                    label="Make me anonymous"
                  />
                </Box>

                {!isFromAnonymous && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <RHFTextField fullWidth name="from" label="From" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <RHFTextField fullWidth name="fromEmail" label="Email Address" />
                    </Grid>
                  </Grid>
                )}
              </>
            )}

            {canShowStep3 && (
              <>
                <Box display="flex" mt={{ xs: 6, sm: 10 }} mb={2} alignItems="center">
                  <Typography mr={1} variant="button" sx={{ color: '#28af23ff', fontWeight: 700 }}>
                    STEP 3 .
                  </Typography>
                  <Typography variant="button" sx={{ fontWeight: 600 }}>
                    Choose a Project & Send Your Gift Card
                  </Typography>
                </Box>

                <ProjectListSection project={project} />

                <Typography variant="body1" px={2} sx={{ mb: { xs: 2, sm: 3 } }}>
                  Donor Details
                </Typography>
                <Paper elevation={0} sx={{ bgcolor: '#fafafaff', borderRadius: 1 }}>
                  <Stack width="100%" alignItems="center">
                    <Box width="100%" maxWidth="md" sx={{ px: { xs: 2, sm: 0 } }}>
                      <FormControlLabel
                        sx={{ mb: 3 }}
                        control={
                          <Checkbox
                            checked={isDonorAnonymous}
                            onChange={(e) => setIsDonorAnonymous(e.target.checked)}
                          />
                        }
                        label="Make me anonymous"
                      />

                      {!isDonorAnonymous && (
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <RHFTextField fullWidth name="firstName" label="First Name" placeholder="First Name" />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <RHFTextField fullWidth name="lastName" label="Last Name" placeholder="Last Name" />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <RHFTextField fullWidth type="email" name="email" label="Email" placeholder="Email" />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <RHFTextField fullWidth type="tel" name="phone" label="Phone Number" placeholder="Phone Number" />
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </Stack>
                </Paper>

                <Box display="flex" justifyContent="center" mt={5}>
                  <LoadingButton loading={isLoading} type="submit" variant="contained" size="large" sx={{ minWidth: 150 }}>
                    SUBMIT
                  </LoadingButton>
                </Box>
              </>
            )}
          </FormProvider>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

const GiftCardDesignItem = ({ src, onClick, selected, disabled }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2,
        transitionDuration: '200ms',
        cursor: 'pointer',
        boxSizing: 'border-box',
        '*': { boxSizing: 'border-box' },
        /* ✅ make tile fill its grid cell responsively */
        width: '100%',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      <Image
        src={src}
        sx={{
          width: '100%',
          /* keep the same aspect ratio as ~250x180 (≈ 5:3.6) */
          aspectRatio: '5 / 3.6',
          objectFit: 'cover',
          borderRadius: 2,
          cursor: 'pointer',
          border: `${(hovered || selected) && !disabled ? '2px' : '0px'} solid #00A852`,
          transitionDuration: '200ms',
          display: 'block',
        }}
      />
      <Box
        sx={{
          width: '100%',
          height: '100%',
          background: '#00A852',
          top: 0,
          left: 0,
          position: 'absolute',
          opacity: (hovered || selected) && !disabled ? 0.2 : 0,
          borderRadius: 2,
          transitionDuration: '200ms',
        }}
      />
    </Box>
  );
};

const ProjectListSection = ({ project }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [showAmountFields] = useState(true);
  const { voucherCode } = useSelector((state) => state.voucher);

  const onAddProject = () => {
    try {
      localStorage.setItem('voucherDonationType', 'giftcard');
    } catch {
      console.error('Failed to store voucher donation type');
    }

    const params = new URLSearchParams(location.search || '');
    params.set('useVoucher', 'true');
    params.set('voucherDonationType', 'giftcard');
    if (voucherCode) params.set('code', String(voucherCode));
    const nextPathname = '/explore-project';
    const nextSearch = `?${params.toString()}`;
    
    const samePath = location.pathname === nextPathname;
    const sameSearch = location.search === nextSearch;
    
    dispatch(setIsGiftCardOpenEvent(false));
    if (!samePath || !sameSearch) {
      navigate({ pathname: nextPathname, search: nextSearch }, { replace: false });
    }
  };

  return (
    <Grid container direction="column" gap={2} px={{ xs: 1, sm: 2 }}>
      {project ? (
        <>
          <Box>
            <Typography variant="body1">Selected Project(s)</Typography>
          </Box>
          <Grid container direction="column" pb={5} pt={2} spacing={2}>
            <Grid item>
              <Card sx={{ p: { xs: 2, sm: 3 }, border: '1px solid #eee', position: 'relative' }}>
                <Grid
                  container
                  spacing={3}
                  sx={{ flexDirection: { xs: 'column', md: 'row' }, pt: { xs: 1, md: 0 } }}
                  alignItems="stretch"
                >
                  <Grid item xs={12} md={3} width="100%" maxWidth="unset">
                    <img
                      width="100%"
                      height="auto"
                      src={project?.mainImage}
                      alt="project-img"
                      style={{
                        borderRadius: 12,
                        objectFit: 'cover',
                        maxHeight: 200,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={8} container spacing={2} direction="column" justifyContent="center" maxWidth="unset">
                    <Grid item>
                      <Typography variant="h5" sx={{ wordBreak: 'break-word' }}>{project?.name}</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {project?.charityDetails.name}
                      </Typography>
                    </Grid>
                    {showAmountFields && (
                      <Grid item container spacing={1} alignItems="center">
                        <Grid item xs={4} sm="auto">
                          <Typography>PHP</Typography>
                        </Grid>
                        <Grid item xs={8} sm>
                          <Box sx={{ position: 'relative' }}>
                            <TextField
                              disabled
                              value={project?.amount}
                              fullWidth
                              inputProps={{
                                sx: {
                                  textAlign: 'right',
                                  ':disabled': { bgcolor: '#e3e3e3' },
                                  borderRadius: 1,
                                  pr: 5, /* room for icon */
                                },
                              }}
                              placeholder="0.00"
                            />
                            <Box
                              component="img"
                              src="/icons/voucher-icon.svg"
                              alt="vi"
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                left: 12,
                                width: 20,
                                height: 20,
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <IconButton
                  aria-label="close"
                  onClick={() => {
                    dispatch(removeAllProjectsEvent());
                  }}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (t) => t.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Card>
            </Grid>
          </Grid>
        </>
      ) : (
        <Grid item>
          <Typography variant="body2">Please select at least one (1) project.</Typography>
          <Grid container justifyContent="center" my={3}>
            <Button onClick={onAddProject} variant="contained">
              ADD PROJECT
            </Button>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const createPayload = ({ formData, projects }) => {
  const payload = {
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    contactNumber: formData.phone,
    donationAmount: 100,
    projects: projects.map((e) => ({
      ...e,
      charityName: e.charityDetails.name,
      id: e._id,
      amount: parseFloat(e.amount),
    })),
  };
  return payload;
};
